/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902313552 — WaitScreen */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../shared/lib/apiClient';
import { OrderDto, toOrder } from '../../../order/lib/to-order';
import { API, IS_DEV } from '../../../../shared/constants';
import { ORDER_ROUTES } from '../../../order/order.constants';
import { useTimer } from '../../../../shared/hooks/useTimer';
import { WAIT_TIMER } from '../../payment.constants';
import { WaitNotice } from '../../payment.types';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { fileSuffix } from '../../../../shared/lib/dev';
import { debug, getVmDetails, screenLog } from '../../../../shared/lib/general';
import { EVENT_NAMES, useSendEventLog, WaitSource } from '../../../event-log';
import { ScreenWrapper } from '../../../../shared/ui';
import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import vmAnimation from '../../../../shared/ui/AnimatedIcon/animations/vm.json';
import { resolveWaitStep } from './wait-step';
import styles from './WaitScreen.module.css';

// wait screen is shown when we are waiting for the response from the server

const WaitScreenBody = () => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler, onError } = useOrderActions();
    const navigate = useNavigate();
    const { state } = useLocation();
    const timerHook = useTimer(WAIT_TIMER);
    const { sendTimeout } = useSendEventLog();

    // WaitScreen is reached from several flows (each payment provider, CT5
    // product selection, recovery). The source — passed via location state at
    // navigation — lets a wait timeout be attributed to the action that stalled.
    const { waitSource, notice } = (state as { waitSource?: WaitSource; notice?: WaitNotice } | null) ?? {};

    const vm = useAppStore((s) => s.vm);
    const order = useAppStore((s) => s.order);
    const setOrder = useAppStore((s) => s.setOrder);
    const [isOrderFinished, setIsOrderFinished] = useState<boolean>(false);

    const {
        isCT5,
        isCT10,
        isPulse,
        treatsProductSelectedAsSuccess,
        treatsConnectedAsSuccess,
        treatsSessionClosedAsSale,
    } = getVmDetails(vm);
    // after payment for CT1 flow is done we show this screen
    // and wait for the response from the server to define which screen to show next
    // if the response is success we show "success screen"
    // if the response is fail we show "fail screen"
    // success means check if the order is completed
    // `devise_status` should change to 4 - means food, snack, coffee,.. was prepared and provided to the customer
    // while `status` should stay 3.
    // if `status` changes to 4 - means that order wasn't completed and we should refund the money

    const getOrderInfo = async (orderId: string) => {
        debug('getOrderInfo', 'api', orderId);
        if (!orderId || orderId === '' || orderId === 'null') {
            debug('no orderId', 'warn');
            return;
        }

        const completedResponseFile = `/dev/order_completed${fileSuffix({
            isCT5,
            isCT10,
        })}.json`;

        const devUrl =
            timerHook.time > 33
                ? `/dev/order_precompleted${isCT5 ? '_ct5' : ''}.json?t=${
                      timerHook.time
                  }`
                : completedResponseFile;

        const orderInfoUrl = IS_DEV
            ? devUrl
            : `${API.ORDER}?id=${orderId}&t=${timerHook.time}`;

        try {
            const { data: dto } = await apiClient.get<OrderDto>(orderInfoUrl);
            const orderInfo = toOrder(dto);
            const { status, device_status, id } = orderInfo;

            if (isCT5) {
                debug('isCT5', 'info');
            }

            const step = resolveWaitStep({
                status,
                deviceStatus: device_status,
                orderId: id,
                timerTime: timerHook.time,
                flow: {
                    isCT5,
                    isCT10,
                    isPulse,
                    treatsProductSelectedAsSuccess,
                    treatsConnectedAsSuccess,
                    treatsSessionClosedAsSale,
                },
            });

            if (isCT5) {
                debug(
                    `∑ CT5: ${step.logMessage} ${step.navigatePath}`,
                    'data',
                );
            }

            if (step.shouldPersistOrder) {
                setOrder(orderInfo);
            }
            if (step.isFinished) {
                setIsOrderFinished(true);
            }
            if (step.logMessage) {
                debug(step.logMessage, 'info');
            }
            if (step.navigatePath) {
                navigate(step.navigatePath, { replace: true });
            }
        } catch {
            onError('Error loading order info - WaitScreen', 'wait');
        }
    };

    useEffect(() => {
        screenLog('WaitScreen');
        timerHook.start();
        debug('render WaitScreen', 'info', order.id);

        return () => {
            timerHook.reset();
        };
    }, []);

    useEffect(() => {
        if (timerHook.time <= 0) {
            debug('timer expired, show fail screen', 'warn');
            sendTimeout({
                name: waitSource
                    ? EVENT_NAMES.waitTimeout[waitSource]
                    : EVENT_NAMES.timeout['wait-screen'],
            });
            onTimerTimeoutHandler('wait', false);
            navigate(
                generatePath(ORDER_ROUTES.failed, { id: String(order.id) }),
                { replace: true },
            );
        } else {
            if (!isOrderFinished) {
                getOrderInfo(`${order.id}`);
            } else {
                // `order completed` is emitted server-side at payment confirmation
                // (SWRN-1639) so it survives the client closing the final screen;
                // no client-side event-log call here on purpose.
                navigate(
                    generatePath(ORDER_ROUTES.success, { id: String(order.id) }),
                    { replace: true },
                );
            }
        }
    }, [timerHook.time]);

    return (
        <ScreenWrapper>
            <h1 className={styles.screenTitle}>{t('Wait')}</h1>
            {notice === 'three-ds' && (
                <p className={styles.notice}>{t('Payment.ThreeDs.ConfirmWithBank')}</p>
            )}
            <div className={styles.contentBox}>
                <AnimatedIcon animationData={vmAnimation} />
            </div>
        </ScreenWrapper>
    );
};

const WaitScreen = withOrderHydration(WaitScreenBody);

export { WaitScreen };
